import * as React from 'react';
import { cn } from '@noor/lib/cn';

/**
 * `InputGroup` — the Figma "Input Group" card (node 732:18134): an h-10 pill
 * on the #d0d5dd hairline whose prefix/suffix cells are divided from the text
 * by a #e4e7ec rule (email icon, country-code select, `http://` prefix, copy
 * suffix).
 *
 * ```tsx
 * <InputGroup>
 *   <InputGroupAddon side="left">
 *     <MailIcon className="size-5 text-form-label-fg" />
 *   </InputGroupAddon>
 *   <InputGroupInput placeholder="info@gmail.com" />
 * </InputGroup>
 * ```
 *
 * The addons are layout slots (divider + centering); their CONTENT (icon,
 * country button, copy action) is the caller's. For RHF forms, bind the text
 * box with `FormField.GroupInput` instead of `InputGroupInput`.
 */
function InputGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="input-group"
      className={cn(
        // Same 40px pill as Input/Select — dimensions resolve through the
        // shared `--field-*` sizing handles (component-sizing.css).
        'flex h-[var(--field-height)] w-full min-w-0 items-center overflow-clip rounded-[var(--field-radius)] border border-form-control-border bg-transparent shadow-field transition-[color,box-shadow] focus-within:border-input-border-focus focus-within:ring-[3px] focus-within:ring-ring/50 has-[input:disabled]:border-form-control-border-disabled has-[input:disabled]:bg-form-control-bg-disabled has-[input:disabled]:text-form-control-fg-disabled has-[input[aria-invalid="true"]]:border-input-border-invalid',
        className,
      )}
      {...props}
    />
  );
}

function InputGroupAddon({
  side,
  className,
  ...props
}: React.ComponentProps<'div'> & {
  /** Which edge the cell docks to (drives the divider side). */
  side: 'left' | 'right';
}) {
  return (
    <div
      data-slot="input-group-addon"
      data-side={side}
      className={cn(
        'flex h-full shrink-0 items-center gap-1 px-[var(--field-addon-padding-x)]',
        side === 'left'
          ? 'border-r border-form-control-border-soft'
          : 'border-l border-form-control-border-soft',
        className,
      )}
      {...props}
    />
  );
}

function InputGroupInput({ className, ...props }: React.ComponentProps<'input'>) {
  return (
    <input
      data-slot="input-group-input"
      className={cn(
        'h-full min-w-0 flex-1 bg-transparent px-[var(--field-padding-x)] text-[length:var(--field-font-size)] font-[var(--field-font-weight)] tracking-[var(--field-tracking)] text-input-fg outline-none placeholder:text-[length:var(--field-font-size)] placeholder:font-[var(--field-font-weight)] placeholder:tracking-[var(--field-tracking)] placeholder:text-input-group-placeholder disabled:cursor-not-allowed disabled:text-form-control-fg-disabled disabled:placeholder:text-form-control-fg-disabled',
        className,
      )}
      {...props}
    />
  );
}

export { InputGroup, InputGroupAddon, InputGroupInput };
